import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  LogOut, Users, UserPlus, KeyRound, Trash2, Upload, Briefcase,
  Loader2, Eye, EyeOff, GraduationCap, DollarSign, Pencil,
} from 'lucide-react';
import BulkStudentUpload from '@/pages/teacher/BulkStudentUpload';
import {
  ACADEMIC_CLASS_LEVELS, ALL_CLASS_LEVELS, isAcademicClass, formatClassLabel,
} from '@/constants/classes';

const SUBJECTS = ['Mathematics', 'Science', 'English', 'Social Studies'];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [classFilter, setClassFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Login Activity
  const [loginActivity, setLoginActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityClassFilter, setActivityClassFilter] = useState('all');

  // Add Student dialog
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '', roll_number: '', class_level: '6', section: 'A',
    username: '', password: 'password123',
  });
  const [adding, setAdding] = useState(false);

  // PIN reset
  const [resetTarget, setResetTarget] = useState(null);
  const [newPin, setNewPin] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  // Bulk CSV dialog
  const [showBulk, setShowBulk] = useState(false);

  // Teachers state + dialogs
  const [teachers, setTeachers] = useState([]);
  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const [teacherForm, setTeacherForm] = useState({
    name: '', username: '', password: 'password123',
    subjects: ['Mathematics'], class_levels: [...ACADEMIC_CLASS_LEVELS],
  });
  const [editTeacher, setEditTeacher] = useState(null);
  const [savingTeacher, setSavingTeacher] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'school_admin') {
      navigate('/admin/login');
      return;
    }
    loadAll();
  }, []);

  const loadLoginActivity = async (classLevel = activityClassFilter) => {
    setLoadingActivity(true);
    try {
      const params = classLevel && classLevel !== 'all' ? `?class_level=${classLevel}` : '';
      const res = await axios.get(`${API}/admin/student-logins${params}`);
      setLoginActivity(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load login activity');
    } finally {
      setLoadingActivity(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'logins') loadLoginActivity(activityClassFilter);
  }, [activeTab, activityClassFilter]);

  const formatLastLogin = (iso) => {
    if (!iso) return { label: 'Never', tone: 'text-gray-400' };
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    let label;
    if (diffMin < 1) label = 'Just now';
    else if (diffMin < 60) label = `${diffMin} min ago`;
    else if (diffHr < 24) label = `${diffHr} hr ago`;
    else if (diffDay < 7) label = `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    else label = d.toLocaleDateString();
    return { label, tone: diffDay < 7 ? 'text-green-700' : 'text-gray-600' };
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statsRes, studentsRes, teachersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard-stats`),
        axios.get(`${API}/teacher/students`),
        axios.get(`${API}/head-teacher/teachers`),
      ]);
      setStats(statsRes.data);
      setStudents(studentsRes.data || []);
      setTeachers(teachersRes.data || []);
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const toggleArr = (arr, val) => arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];

  const handleAddTeacher = async () => {
    if (!teacherForm.name.trim() || !teacherForm.username.trim()) {
      toast.error('Name and Username are required');
      return;
    }
    if (!teacherForm.subjects.length) { toast.error('Select at least one subject'); return; }
    if (!teacherForm.class_levels.length) { toast.error('Select at least one class'); return; }
    setSavingTeacher(true);
    try {
      const res = await axios.post(`${API}/head-teacher/teachers/add`, teacherForm);
      toast.success(`Teacher ${teacherForm.name} added (${teacherForm.username} / ${teacherForm.password})`, { duration: 8000 });
      setShowAddTeacher(false);
      setTeacherForm({ name: '', username: '', password: 'password123', subjects: ['Mathematics'], class_levels: [...ACADEMIC_CLASS_LEVELS] });
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add teacher');
    } finally {
      setSavingTeacher(false);
    }
  };

  const handleSaveTeacher = async () => {
    if (!editTeacher) return;
    setSavingTeacher(true);
    try {
      await axios.put(`${API}/head-teacher/teachers/${editTeacher.id}`, {
        name: editTeacher.name,
        subjects: editTeacher.subjects,
        class_levels: editTeacher.class_levels,
      });
      toast.success('Teacher updated');
      setEditTeacher(null);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update teacher');
    } finally {
      setSavingTeacher(false);
    }
  };

  const handleDeleteTeacher = async (t) => {
    if (!window.confirm(`Delete teacher ${t.name}? This will remove their login account too.`)) return;
    try {
      await axios.delete(`${API}/head-teacher/teachers/${t.id}`);
      toast.success(`${t.name} deleted`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleAddStudent = async () => {
    if (!addForm.name || !addForm.roll_number) {
      toast.error('Name and Roll Number are required');
      return;
    }
    if (isAcademicClass(addForm.class_level) && !addForm.username) {
      toast.error('Username is required for Classes 6-10');
      return;
    }
    setAdding(true);
    try {
      const payload = { ...addForm };
      if (!isAcademicClass(addForm.class_level)) {
        delete payload.username;
        delete payload.password;
      }
      const res = await axios.post(`${API}/teacher/students/add`, payload);
      if (res.data.student?.has_login) {
        toast.success(`${addForm.name} added with login (${res.data.student.username} / ${res.data.student.password})`, { duration: 8000 });
      } else {
        toast.success(`${addForm.name} added (admin-only — no student login created)`);
      }
      setShowAdd(false);
      setAddForm({ name: '', roll_number: '', class_level: '6', section: 'A', username: '', password: 'password123' });
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add student');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete ${s.name} (Roll ${s.roll_number})? This is permanent.`)) return;
    try {
      await axios.delete(`${API}/teacher/students/${s.id}`);
      toast.success(`${s.name} deleted`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleResetPin = async () => {
    if (!resetTarget) return;
    setResetting(true);
    setResetResult(null);
    try {
      const res = await axios.post(`${API}/admin/reset-student-pin`, {
        student_id: resetTarget.id,
        new_password: newPin || undefined,
      });
      setResetResult(res.data);
      toast.success('PIN reset — share new password with student/parent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const filteredStudents = students.filter((s) => {
    if (classFilter !== 'all' && s.class_level !== classFilter) return false;
    const term = search.trim().toLowerCase();
    if (term && !((s.name || '').toLowerCase().includes(term) || String(s.roll_number || '').toLowerCase().includes(term))) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-amber-100 shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-amber-900" data-testid="admin-dashboard-title">Office Admin Portal</h1>
              <p className="text-xs text-amber-700">Admissions · Fees · PINs · Attendance · Parent contacts</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden md:inline text-sm text-gray-600">{user?.name}</span>
            <Button variant="outline" size="sm" onClick={() => { logout(); navigate('/admin/login'); }} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
        {/* Tabs */}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 -mb-px flex gap-2 overflow-x-auto">
          {[
            { k: 'overview', label: 'Overview' },
            { k: 'students', label: 'Students' },
            { k: 'teachers', label: 'Teachers' },
            { k: 'logins', label: 'Login Activity' },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setActiveTab(t.k)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === t.k ? 'border-amber-600 text-amber-700' : 'border-transparent text-gray-500 hover:text-amber-700'
              }`}
              data-testid={`admin-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>
        ) : (
          <>
            {activeTab === 'overview' && stats && (
              <div className="space-y-6" data-testid="admin-overview-section">
                {/* Stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card data-testid="stat-total-students" className="border-amber-100">
                    <CardContent className="p-5 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Students</p>
                        <p className="text-3xl font-bold text-amber-900">{stats.total_students}</p>
                      </div>
                      <Users className="w-10 h-10 text-amber-500 opacity-60" />
                    </CardContent>
                  </Card>
                  <Card className="border-emerald-100">
                    <CardContent className="p-5">
                      <p className="text-sm text-gray-500">Academic (6-10)</p>
                      <p className="text-3xl font-bold text-emerald-700">{stats.academic_students}</p>
                      <p className="text-xs text-gray-400 mt-1">Have student login</p>
                    </CardContent>
                  </Card>
                  <Card className="border-blue-100">
                    <CardContent className="p-5">
                      <p className="text-sm text-gray-500">Admin-only</p>
                      <p className="text-3xl font-bold text-blue-700">{stats.admin_only_students}</p>
                      <p className="text-xs text-gray-400 mt-1">LKG-5, 10 — no login</p>
                    </CardContent>
                  </Card>
                  <Card className="border-purple-100" data-testid="stat-teachers">
                    <CardContent className="p-5 flex items-center justify-between cursor-pointer" onClick={() => setActiveTab('teachers')}>
                      <div>
                        <p className="text-sm text-gray-500">Teachers</p>
                        <p className="text-3xl font-bold text-purple-700">{stats.teachers_count ?? 0}</p>
                      </div>
                      <GraduationCap className="w-10 h-10 text-purple-400 opacity-60" />
                    </CardContent>
                  </Card>
                  <Card className="border-red-100">
                    <CardContent className="p-5 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Pending Fees</p>
                        <p className="text-3xl font-bold text-red-700">{stats.pending_fees_count}</p>
                      </div>
                      <DollarSign className="w-10 h-10 text-red-400 opacity-60" />
                    </CardContent>
                  </Card>
                </div>

                {/* By-class breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Students by Class</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                      {ALL_CLASS_LEVELS.map(cls => (
                        <div
                          key={cls}
                          className={`rounded-lg p-3 border ${isAcademicClass(cls) ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
                          data-testid={`admin-class-card-${cls}`}
                        >
                          <p className="text-xs text-gray-600">{formatClassLabel(cls)}</p>
                          <p className="text-xl font-bold text-gray-900">{stats.by_class[cls] || 0}</p>
                          <p className="text-[10px] text-gray-500 mt-1">{isAcademicClass(cls) ? 'Academic' : 'Admin-only'}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick actions */}
                <Card>
                  <CardHeader><CardTitle className="text-base">Quick Actions</CardTitle></CardHeader>
                  <CardContent className="flex flex-wrap gap-3">
                    <Button onClick={() => setShowAdd(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="quick-add-student">
                      <UserPlus className="w-4 h-4 mr-2" /> Add Single Student
                    </Button>
                    <Button onClick={() => setShowBulk(true)} variant="outline" data-testid="quick-bulk-upload">
                      <Upload className="w-4 h-4 mr-2" /> Bulk Upload (CSV)
                    </Button>
                    <Button onClick={() => setActiveTab('students')} variant="outline" data-testid="quick-manage-students">
                      <Users className="w-4 h-4 mr-2" /> Manage Students
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'students' && (
              <div className="space-y-4" data-testid="admin-students-section">
                <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg p-3 border">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-600">Class</Label>
                    <select
                      value={classFilter}
                      onChange={(e) => setClassFilter(e.target.value)}
                      className="border rounded-md px-2 py-1 text-sm bg-white"
                      data-testid="admin-class-filter"
                    >
                      <option value="all">All</option>
                      {ALL_CLASS_LEVELS.map(c => <option key={c} value={c}>{formatClassLabel(c)}</option>)}
                    </select>
                  </div>
                  <Input
                    placeholder="Search by name or roll…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="max-w-xs"
                    data-testid="admin-students-search"
                  />
                  <div className="ml-auto flex gap-2">
                    <Button size="sm" onClick={() => setShowAdd(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="add-student-btn">
                      <UserPlus className="w-4 h-4 mr-2" /> Add Student
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowBulk(true)} data-testid="bulk-upload-btn">
                      <Upload className="w-4 h-4 mr-2" /> CSV
                    </Button>
                  </div>
                </div>

                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-amber-50 text-amber-900">
                          <tr>
                            <th className="text-left px-4 py-2">Name</th>
                            <th className="text-left px-4 py-2">Roll</th>
                            <th className="text-left px-4 py-2">Class</th>
                            <th className="text-left px-4 py-2">Section</th>
                            <th className="text-left px-4 py-2">Login</th>
                            <th className="text-right px-4 py-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredStudents.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-8 text-gray-400">No students match the filter</td></tr>
                          ) : filteredStudents.map(s => (
                            <tr key={s.id} className="border-t hover:bg-amber-50/50" data-testid={`admin-student-row-${s.id}`}>
                              <td className="px-4 py-2 font-medium text-gray-800">{s.name}</td>
                              <td className="px-4 py-2 text-gray-600">{s.roll_number}</td>
                              <td className="px-4 py-2"><Badge variant="outline">{formatClassLabel(s.class_level)}</Badge></td>
                              <td className="px-4 py-2 text-gray-600">{s.section || 'A'}</td>
                              <td className="px-4 py-2">
                                {isAcademicClass(s.class_level) ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{s.username}</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-gray-500">no login</Badge>
                                )}
                              </td>
                              <td className="px-4 py-2 text-right space-x-1">
                                {isAcademicClass(s.class_level) && (
                                  <Button
                                    size="sm" variant="outline"
                                    onClick={() => { setResetTarget(s); setNewPin(''); setResetResult(null); }}
                                    data-testid={`reset-pin-${s.id}`}
                                  >
                                    <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset PIN
                                  </Button>
                                )}
                                <Button
                                  size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handleDelete(s)}
                                  data-testid={`delete-student-${s.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'teachers' && (
              <div className="space-y-4" data-testid="admin-teachers-section">
                <div className="flex items-center justify-between bg-white rounded-lg p-3 border">
                  <div>
                    <h3 className="font-semibold text-amber-900">Teachers ({teachers.length})</h3>
                    <p className="text-xs text-gray-500">Add, edit, or remove teachers. Both you and the Principal can manage teachers.</p>
                  </div>
                  <Button onClick={() => setShowAddTeacher(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="add-teacher-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Add Teacher
                  </Button>
                </div>

                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-amber-50 text-amber-900">
                          <tr>
                            <th className="text-left px-4 py-2">Name</th>
                            <th className="text-left px-4 py-2">Login</th>
                            <th className="text-left px-4 py-2">Subjects</th>
                            <th className="text-left px-4 py-2">Classes</th>
                            <th className="text-right px-4 py-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teachers.length === 0 ? (
                            <tr><td colSpan={5} className="text-center py-8 text-gray-400">No teachers yet. Click &quot;Add Teacher&quot; to get started.</td></tr>
                          ) : teachers.map(t => (
                            <tr key={t.id} className="border-t hover:bg-amber-50/50" data-testid={`admin-teacher-row-${t.id}`}>
                              <td className="px-4 py-2 font-medium text-gray-800">{t.name}</td>
                              <td className="px-4 py-2"><Badge variant="outline" className="text-emerald-700 border-emerald-200">{t.username}</Badge></td>
                              <td className="px-4 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {(t.subjects || []).map(s => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}
                                </div>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {(t.class_levels || []).map(cl => <Badge key={cl} variant="outline" className="text-xs">{formatClassLabel(cl)}</Badge>)}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-right space-x-1">
                                <Button size="sm" variant="outline" onClick={() => setEditTeacher({ ...t, subjects: t.subjects || [], class_levels: t.class_levels || [] })} data-testid={`edit-teacher-${t.id}`}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-amber-700 border-amber-200 hover:bg-amber-50"
                                  data-testid={`reset-teacher-${t.username}`}
                                  onClick={async () => {
                                    if (!window.confirm(`Reset password for ${t.name} to Teacher@123?\n\nThey will be forced to set a new password on their next login.`)) return;
                                    try {
                                      const res = await axios.post(`${API}/head-teacher/teachers/${t.id}/reset-password`);
                                      toast.success(res.data?.message || 'Password reset');
                                      window.alert(`✅ Password reset successful\n\nUsername: ${res.data.username}\nNew password: ${res.data.password}\n\nShare these with ${t.name}. They will be asked to set their own password on first login.`);
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Reset failed');
                                    }
                                  }}
                                >
                                  <KeyRound className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteTeacher(t)} data-testid={`delete-teacher-${t.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'logins' && (
              <div className="space-y-4" data-testid="admin-logins-section">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <CardTitle className="text-lg">Student Login Activity</CardTitle>
                        <p className="text-sm text-gray-500 mt-1">
                          {loginActivity ? (
                            <>
                              <span className="text-green-700 font-semibold">{loginActivity.logged_in_count}</span> logged in
                              {' · '}
                              <span className="text-gray-600 font-semibold">{loginActivity.never_logged_in}</span> never logged in
                              {' · '}
                              <span className="text-gray-500">{loginActivity.total} total</span>
                            </>
                          ) : 'Loading…'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-gray-600">Class:</Label>
                        <select
                          value={activityClassFilter}
                          onChange={(e) => setActivityClassFilter(e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                          data-testid="admin-logins-class-filter"
                        >
                          <option value="all">All Classes</option>
                          {ACADEMIC_CLASS_LEVELS.map(cl => (
                            <option key={cl} value={cl}>{formatClassLabel(cl)}</option>
                          ))}
                        </select>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => loadLoginActivity(activityClassFilter)}
                          disabled={loadingActivity}
                          data-testid="admin-logins-refresh"
                        >
                          {loadingActivity ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold">Name</th>
                            <th className="text-left px-3 py-2 font-semibold">Username</th>
                            <th className="text-left px-3 py-2 font-semibold">Class · Roll</th>
                            <th className="text-left px-3 py-2 font-semibold">Last Login</th>
                            <th className="text-center px-3 py-2 font-semibold">Total Logins</th>
                            <th className="text-left px-3 py-2 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingActivity ? (
                            <tr><td colSpan="6" className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline text-amber-600" /></td></tr>
                          ) : !loginActivity || loginActivity.students.length === 0 ? (
                            <tr><td colSpan="6" className="text-center py-8 text-gray-400">No students found</td></tr>
                          ) : loginActivity.students.map((s) => {
                            const ll = formatLastLogin(s.last_login_at);
                            return (
                              <tr key={s.student_id} className="border-b hover:bg-gray-50" data-testid={`admin-login-row-${s.username}`}>
                                <td className="px-3 py-2 font-medium">{s.name}</td>
                                <td className="px-3 py-2 text-gray-600">{s.username}</td>
                                <td className="px-3 py-2 text-gray-600">Class {s.class_level}{s.section ? ` · ${s.section}` : ''} · Roll {s.roll_number}</td>
                                <td className={`px-3 py-2 ${ll.tone}`}>{ll.label}</td>
                                <td className="px-3 py-2 text-center">{s.login_count || 0}</td>
                                <td className="px-3 py-2">
                                  {s.must_change_password ? (
                                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">Must reset password</Badge>
                                  ) : s.last_login_at ? (
                                    <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-gray-500">Never logged in</Badge>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

          </>
        )}
      </main>

      {/* Add Teacher Dialog */}
      <Dialog open={showAddTeacher} onOpenChange={setShowAddTeacher}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Teacher</DialogTitle>
            <DialogDescription>
              Teachers academically teach Classes 6-10. Subjects and class assignments can be edited later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Name *</Label>
              <Input value={teacherForm.name} onChange={(e) => setTeacherForm({ ...teacherForm, name: e.target.value })} data-testid="teacher-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Username *</Label>
                <Input value={teacherForm.username} onChange={(e) => setTeacherForm({ ...teacherForm, username: e.target.value })} data-testid="teacher-form-username" />
              </div>
              <div>
                <Label>Password</Label>
                <Input value={teacherForm.password} onChange={(e) => setTeacherForm({ ...teacherForm, password: e.target.value })} data-testid="teacher-form-password" />
              </div>
            </div>
            <div>
              <Label>Subjects *</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {SUBJECTS.map(s => (
                  <button
                    key={s} type="button"
                    onClick={() => setTeacherForm({ ...teacherForm, subjects: toggleArr(teacherForm.subjects, s) })}
                    className={`px-3 py-1 rounded-full text-xs border ${teacherForm.subjects.includes(s) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300'}`}
                  >{s}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Classes *</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ACADEMIC_CLASS_LEVELS.map(cl => (
                  <button
                    key={cl} type="button"
                    onClick={() => setTeacherForm({ ...teacherForm, class_levels: toggleArr(teacherForm.class_levels, cl) })}
                    className={`px-3 py-1 rounded-full text-xs border ${teacherForm.class_levels.includes(cl) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300'}`}
                  >{formatClassLabel(cl)}</button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">Teachers academically teach only Classes 6-10.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTeacher(false)}>Cancel</Button>
            <Button onClick={handleAddTeacher} disabled={savingTeacher} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-add-teacher">
              {savingTeacher ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Teacher'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Teacher Dialog */}
      <Dialog open={!!editTeacher} onOpenChange={(o) => { if (!o) setEditTeacher(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Teacher</DialogTitle>
            <DialogDescription>{editTeacher?.username}</DialogDescription>
          </DialogHeader>
          {editTeacher && (
            <div className="space-y-3 py-2">
              <div>
                <Label>Name</Label>
                <Input value={editTeacher.name} onChange={(e) => setEditTeacher({ ...editTeacher, name: e.target.value })} data-testid="edit-teacher-name" />
              </div>
              <div>
                <Label>Subjects</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {SUBJECTS.map(s => (
                    <button
                      key={s} type="button"
                      onClick={() => setEditTeacher({ ...editTeacher, subjects: toggleArr(editTeacher.subjects, s) })}
                      className={`px-3 py-1 rounded-full text-xs border ${editTeacher.subjects.includes(s) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300'}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Classes</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {ACADEMIC_CLASS_LEVELS.map(cl => (
                    <button
                      key={cl} type="button"
                      onClick={() => setEditTeacher({ ...editTeacher, class_levels: toggleArr(editTeacher.class_levels, cl) })}
                      className={`px-3 py-1 rounded-full text-xs border ${editTeacher.class_levels.includes(cl) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300'}`}
                    >{formatClassLabel(cl)}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTeacher(null)}>Cancel</Button>
            <Button onClick={handleSaveTeacher} disabled={savingTeacher} className="bg-amber-600 hover:bg-amber-700" data-testid="save-teacher">
              {savingTeacher ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Student Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Student</DialogTitle>
            <DialogDescription>
              For Classes 6-10 a student login is created. For LKG/UKG/1-5 it&apos;s admin-only — no login needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="add-form-name" />
              </div>
              <div>
                <Label>Roll No *</Label>
                <Input value={addForm.roll_number} onChange={(e) => setAddForm({ ...addForm, roll_number: e.target.value })} data-testid="add-form-roll" />
              </div>
              <div>
                <Label>Class *</Label>
                <select
                  value={addForm.class_level}
                  onChange={(e) => setAddForm({ ...addForm, class_level: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm bg-white mt-0.5"
                  data-testid="add-form-class"
                >
                  {ALL_CLASS_LEVELS.map(c => <option key={c} value={c}>{formatClassLabel(c)}</option>)}
                </select>
              </div>
              <div>
                <Label>Section</Label>
                <select
                  value={addForm.section}
                  onChange={(e) => setAddForm({ ...addForm, section: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm bg-white mt-0.5"
                  data-testid="add-form-section"
                >
                  {['A','B','C','D','E'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {isAcademicClass(addForm.class_level) ? (
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <div>
                  <Label>Login Username *</Label>
                  <Input value={addForm.username} onChange={(e) => setAddForm({ ...addForm, username: e.target.value })} data-testid="add-form-username" />
                </div>
                <div>
                  <Label>Login Password</Label>
                  <Input value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} data-testid="add-form-password" />
                </div>
              </div>
            ) : (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800">
                {formatClassLabel(addForm.class_level)} is admin-only — no student login will be created. You&apos;ll still track attendance, fees and profile here.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAddStudent} disabled={adding} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-add-student">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Student'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulk} onOpenChange={setShowBulk}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Upload Students (CSV)</DialogTitle>
            <DialogDescription>
              CSV columns: name, roll_number, class_level [, section, username, password]. For LKG/UKG/1-5, leave username/password blank — no login is created.
            </DialogDescription>
          </DialogHeader>
          <BulkStudentUpload onComplete={() => { setShowBulk(false); loadAll(); }} />
        </DialogContent>
      </Dialog>

      {/* Reset PIN Dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setResetResult(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Student PIN</DialogTitle>
            <DialogDescription>
              {resetTarget?.name} · Roll {resetTarget?.roll_number} · {formatClassLabel(resetTarget?.class_level || '')}
            </DialogDescription>
          </DialogHeader>
          {!resetResult ? (
            <div className="space-y-3 py-2">
              <div>
                <Label>New Password (or leave blank to auto-generate)</Label>
                <Input
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  placeholder="Leave blank for random"
                  data-testid="reset-pin-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
                <Button onClick={handleResetPin} disabled={resetting} className="bg-amber-600 hover:bg-amber-700" data-testid="reset-pin-submit">
                  {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset PIN'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                <p className="text-sm font-semibold text-emerald-800">New password generated</p>
                <p className="text-2xl font-mono font-bold text-emerald-900 mt-2">{resetResult.new_password}</p>
                <p className="text-xs text-emerald-700 mt-2">
                  Share with student/parent. They&apos;ll be asked to change it on next login.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={() => { setResetTarget(null); setResetResult(null); }} className="w-full">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
